import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileShopService } from '../../api/generated/services/MobileShopService';
import type { JtlOrderView } from '../../api/generated/models/JtlOrderView';
import type { ShopifyOrderView } from '../../api/generated/models/ShopifyOrderView';
import Pagination from '../Pagination';
import { ApiError } from '../../api/generated/core/ApiError';
import { helper } from '../../utils/helper';
import { OrderDetailSidebar } from './OrderDetailSidebar';
import type { PeriodFilterValue } from './PeriodFilterDropdown';

const ORDERS_PAGE_SIZE = 6;
const PER_SHOP_PAGE_SIZE = 10;
type OrderTab = 'all' | 'unshipped' | 'unpaid';
type UnifiedOrder = JtlOrderView | ShopifyOrderView;

interface ShopOrdersCache {
  orders: UnifiedOrder[];
  total: number;
}

interface OrdersWidgetProps {
  shopIds: string[];
  periodValue?: PeriodFilterValue | null;
  refreshKey?: number;
}

function buildPeriodFilters(pv: PeriodFilterValue | null | undefined): Array<{ key?: string; value?: string }> {
  if (!pv) return [];
  const out: Array<{ key?: string; value?: string }> = [];
  if (pv.preset) {
    const presetToDays: Record<string, string> = { today: '1', '7': '7', '30': '30' };
    if (presetToDays[pv.preset]) {
      out.push({ key: 'period', value: presetToDays[pv.preset] });
    } else {
      const now = new Date();
      let start: Date;
      let end: Date = now;
      switch (pv.preset) {
        case 'this_month': start = new Date(now.getFullYear(), now.getMonth(), 1); break;
        case 'last_month': start = new Date(now.getFullYear(), now.getMonth() - 1, 1); end = new Date(now.getFullYear(), now.getMonth(), 0); break;
        case 'this_year': start = new Date(now.getFullYear(), 0, 1); break;
        default: start = new Date(now.getFullYear(), now.getMonth(), 1);
      }
      const fmt = (d: Date) => d.toISOString().slice(0, 10);
      out.push({ key: 'startDate', value: fmt(start) });
      out.push({ key: 'endDate', value: fmt(end) });
    }
  } else if (pv.startDate && pv.endDate) {
    out.push({ key: 'startDate', value: pv.startDate });
    out.push({ key: 'endDate', value: pv.endDate });
  }
  return out;
}

function mergeAndSort(cache: Record<string, ShopOrdersCache>, shopIds: string[]): UnifiedOrder[] {
  const combined: UnifiedOrder[] = [];
  for (const sid of shopIds) {
    const c = cache[sid];
    if (c) combined.push(...c.orders);
  }
  combined.sort((a, b) => {
    const da = a.orderDate ?? '';
    const db = b.orderDate ?? '';
    return db.localeCompare(da);
  });
  return combined;
}

export const OrdersWidget: React.FC<OrdersWidgetProps> = ({ shopIds, periodValue, refreshKey = 0 }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<OrderTab>('all');
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState<UnifiedOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [ordersCacheByShop, setOrdersCacheByShop] = useState<Record<string, ShopOrdersCache>>({});
  const cacheRef = useRef<Record<string, ShopOrdersCache>>({});
  const fetchKeyRef = useRef(0);

  const shopIdsKey = shopIds.join(',');

  cacheRef.current = ordersCacheByShop;

  useEffect(() => {
    setOrdersCacheByShop({});
    setPage(1);
  }, [shopIdsKey, tab, periodValue, refreshKey]);

  useEffect(() => {
    if (shopIds.length === 0) {
      setOrders([]);
      setTotal(0);
      setTotalPages(1);
      return;
    }
    setLoading(true);
    setError(null);
    const periodFilters = buildPeriodFilters(periodValue);
    const statusFilter = tab !== 'all' ? [{ key: 'status' as const, value: tab }] : [];
    const baseFilters = [...statusFilter, ...periodFilters];

    if (shopIds.length === 1) {
      const filters: Array<{ key?: string; value?: string }> = [
        { key: 'page', value: String(page) },
        { key: 'limit', value: String(ORDERS_PAGE_SIZE) },
        ...baseFilters,
      ];
      MobileShopService.postAppMobileShopOrdersList(shopIds[0], filters as any)
        .then((result) => {
          setOrders(result.orders ?? []);
          setTotal(result.totalOrders ?? 0);
          setTotalPages(result.totalPages ?? 1);
        })
        .catch((err) => {
          setOrders([]);
          setTotal(0);
          setTotalPages(1);
          setError(err instanceof ApiError ? (err.body?.message ?? err.message) : String(err));
        })
        .finally(() => setLoading(false));
      return;
    }

    const currentFetchKey = fetchKeyRef.current + 1;
    fetchKeyRef.current = currentFetchKey;
    const cache = cacheRef.current;
    const allShopsHaveCache = shopIds.length === 0 || shopIds.every((sid) => cache[sid] !== undefined);
    const merged = mergeAndSort(cache, shopIds);
    const needCount = page * ORDERS_PAGE_SIZE;
    const totalSum = shopIds.reduce((acc, sid) => acc + (cache[sid]?.total ?? 0), 0);
    const pages = Math.max(1, Math.ceil(totalSum / ORDERS_PAGE_SIZE));

    if (!allShopsHaveCache) {
      const promises = shopIds.map((sid) => {
        const filters: Array<{ key?: string; value?: string }> = [
          { key: 'page', value: '1' },
          { key: 'limit', value: String(PER_SHOP_PAGE_SIZE) },
          ...baseFilters,
        ];
        return MobileShopService.postAppMobileShopOrdersList(sid, filters as any)
          .then((r) => ({ shopId: sid, orders: r.orders ?? [], total: r.totalOrders ?? 0 }))
          .catch(() => ({ shopId: sid, orders: [] as UnifiedOrder[], total: 0 }));
      });
      Promise.all(promises).then((results) => {
        if (currentFetchKey !== fetchKeyRef.current) {
          setLoading(false);
          return;
        }
        const next: Record<string, ShopOrdersCache> = {};
        for (const r of results) {
          next[r.shopId] = { orders: r.orders, total: r.total };
        }
        setOrdersCacheByShop(next);
        setLoading(false);
      });
      return;
    }

    if (merged.length >= needCount) {
      const start = (page - 1) * ORDERS_PAGE_SIZE;
      setOrders(merged.slice(start, start + ORDERS_PAGE_SIZE));
      setTotal(totalSum);
      setTotalPages(pages);
      setLoading(false);
      return;
    }

    const shopsToFetch = shopIds.filter((sid) => {
      const c = cache[sid];
      if (!c) return true;
      return c.orders.length < c.total;
    });

    if (shopsToFetch.length === 0) {
      const start = (page - 1) * ORDERS_PAGE_SIZE;
      setOrders(merged.slice(start, start + ORDERS_PAGE_SIZE));
      setTotal(totalSum);
      setTotalPages(pages);
      setLoading(false);
      return;
    }

    const promises = shopsToFetch.map((sid) => {
      const c = cache[sid];
      const pageToFetch = c ? Math.floor(c.orders.length / PER_SHOP_PAGE_SIZE) + 1 : 1;
      const filters: Array<{ key?: string; value?: string }> = [
        { key: 'page', value: String(pageToFetch) },
        { key: 'limit', value: String(PER_SHOP_PAGE_SIZE) },
        ...baseFilters,
      ];
      return MobileShopService.postAppMobileShopOrdersList(sid, filters as any)
        .then((r) => ({ shopId: sid, orders: r.orders ?? [], total: r.totalOrders ?? 0 }))
        .catch(() => ({ shopId: sid, orders: [] as UnifiedOrder[], total: cache[sid]?.total ?? 0 }));
    });

    Promise.all(promises).then((results) => {
      if (currentFetchKey !== fetchKeyRef.current) {
        setLoading(false);
        return;
      }
      setOrdersCacheByShop((prev) => {
        const next: Record<string, ShopOrdersCache> = { ...prev };
        for (const r of results) {
          const existing = next[r.shopId];
          if (existing) {
            next[r.shopId] = {
              orders: [...existing.orders, ...r.orders],
              total: r.total,
            };
          } else {
            next[r.shopId] = { orders: [...r.orders], total: r.total };
          }
        }
        return next;
      });
      setLoading(false);
    });
  }, [shopIdsKey, tab, page, periodValue, ordersCacheByShop]);

  useEffect(() => {
    if (shopIds.length <= 1) return;
    const cache = cacheRef.current;
    const hasAny = shopIds.some((sid) => cache[sid]);
    if (hasAny) {
      const merged = mergeAndSort(cache, shopIds);
      const needCount = page * ORDERS_PAGE_SIZE;
      const totalSum = shopIds.reduce((acc, sid) => acc + (cache[sid]?.total ?? 0), 0);
      const pages = Math.max(1, Math.ceil(totalSum / ORDERS_PAGE_SIZE));
      const start = (page - 1) * ORDERS_PAGE_SIZE;
      setOrders(merged.slice(start, start + ORDERS_PAGE_SIZE));
      setTotal(totalSum);
      setTotalPages(pages);
    }
  }, [ordersCacheByShop, shopIdsKey, page]);

  const handleOrderClick = useCallback((shopId: string, orderId: number) => {
    setSelectedShopId(shopId);
    setSelectedOrderId(orderId);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setSelectedOrderId(null);
    setSelectedShopId(null);
  }, []);

  const startItem = total === 0 ? 0 : (page - 1) * ORDERS_PAGE_SIZE + 1;
  const endItem = Math.min(page * ORDERS_PAGE_SIZE, total);

  if (shopIds.length === 0) {
    return (
      <div className="stat-card">
        <div className="stat-card-header">
          <h3 className="stat-card-title">{t('dashboard_stats_orders')}</h3>
        </div>
        <p className="stat-metric-label">{t('dashboard_stats_select_shop')}</p>
      </div>
    );
  }

  const showNoOrders = !loading && !error && orders.length === 0 && total === 0;

  return (
    <div className="stat-card stat-card--orders">
      <div className="stat-card-header">
        <h3 className="stat-card-title">{t('dashboard_stats_orders')}</h3>
        <div className="orders-tabs">
          <button type="button" className={`orders-tab ${tab === 'all' ? 'active' : ''}`} onClick={() => { setTab('all'); setPage(1); }}>
            {t('dashboard_stats_orders_all')}
          </button>
          <button type="button" className={`orders-tab ${tab === 'unshipped' ? 'active' : ''}`} onClick={() => { setTab('unshipped'); setPage(1); }}>
            {t('dashboard_stats_orders_unshipped')}
          </button>
          <button type="button" className={`orders-tab ${tab === 'unpaid' ? 'active' : ''}`} onClick={() => { setTab('unpaid'); setPage(1); }}>
            {t('dashboard_stats_orders_unpaid')}
          </button>
        </div>
      </div>
      {error && <p className="dashboard-error-inline">{error}</p>}
      {!error && (
        <div className="orders-body">
          {loading ? (
            <div className="orders-loader">
              <div className="order-sidebar-spinner" />
            </div>
          ) : showNoOrders ? (
            <p className="orders-empty">{t('dashboard_stats_no_orders')}</p>
          ) : (
            <div className="orders-grid">
              {orders.map((order) => (
                <div
                  key={`${order.shopId}-${order.id}`}
                  className="order-item order-item--clickable"
                  onClick={() => handleOrderClick(order.shopId, order.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleOrderClick(order.shopId, order.id); }}
                >
                  {order.thumbnail ? (
                    <img src={order.thumbnail} alt="" className="order-item-image" />
                  ) : (
                    <div className="order-item-image">-</div>
                  )}
                  <div className="order-item-details">
                    <div className="order-item-title">
                      {t('dashboard_order_label')}: {order.orderNumber ?? order.id}
                    </div>
                    <div className="order-item-meta">
                      {t('dashboard_order_price')}: {order.totalPriceGross?.amount != null
                        ? helper.formatPrice({ amount: Math.round(order.totalPriceGross.amount * 100), currency: order.totalPriceGross.currencyIso ?? 'EUR' })
                        : '-'}
                    </div>
                    <div className="order-item-meta">
                      {t('dashboard_order_date')}: {order.orderDate ? helper.formatDate(order.orderDate) : '-'}
                    </div>
                    <div className="order-item-status">{order.status ?? (order.isShipped ? 'SHIPPED' : 'IN PROCESS')}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pagination
            startItem={startItem}
            endItem={endItem}
            total={total}
            totalPages={totalPages}
            page={page}
            loading={loading}
            handlePageChange={(dir) => setPage((p) => (dir === 'next' ? Math.min(p + 1, totalPages) : Math.max(1, p - 1)))}
          />
        </div>
      )}

      {selectedOrderId !== null && selectedShopId && (
        <OrderDetailSidebar
          shopId={selectedShopId}
          orderId={selectedOrderId}
          onClose={handleCloseSidebar}
        />
      )}
    </div>
  );
};
